import React from 'react';

import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import * as motion from 'motion/react-m';

import {FlyoutAnimation} from '../../../animations/flyout';
import {CustomScrollSelect} from '../../../layout/CustomScrollSelect/CustomScrollSelect';

import {useOptionList} from './useOptionList';

import type {WithChildren} from '../../../../utils/childTypes';

/**
 * Interface for the properties of the `OptionList` component.
 * It provides options to control the display and behavior of a list of options, such as width, position, and height.
 */
export interface ComponentProps {
    /**
     * A boolean flag indicating whether the option list should span the full width of its container.
     * If true, the list will take up the entire available width.
     *
     * @default false
     */
    hasFullWidth?: boolean;
    /**
     * A boolean flag indicating whether the option list should be scrollable.
     * If true, the list will have a scroll bar when its content exceeds the specified height.
     *
     * @default true
     */
    hasScroll?: boolean;
    /**
     * The height of the option list, which controls the vertical size of the list.
     * This value is useful when making the list scrollable.
     */
    height?: number;
    /**
     * A boolean flag indicating whether the option list should be displayed on top of other elements.
     * If true, the list will render above other elements in the layout.
     *
     * @default false
     */
    isOnTop?: boolean;
    /**
     * The offset of the option list.
     */
    offset?: number;
    /**
     * The position of the option list relative to its container.
     * It can be aligned to the `left`, `right`, or `center`.
     *
     * @default 'left'
     */
    position?: 'center' | 'left' | 'right';
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'OptionList'
     */
    testId?: string;
}

/**
 * The `OptionList` component is a wrapper that displays a list of selectable options. It supports various configurations
 * such as adjusting its position, width, and height. It can also adapt to whether it should display on top of other elements.
 * The component utilizes an animation for the flyout effect when the options are displayed and provides a custom scroll view.
 *
 * @param props              The component props.
 * @param props.children     The list of child elements to be displayed as options.
 * @param props.hasFullWidth A boolean flag indicating whether the option list should span the full width of its container.
 * @param props.height       The height of the option list, allowing for a scrollable view.
 * @param props.offset       The offset of the option list.
 * @param props.isOnTop      A boolean flag indicating whether the option list is positioned on top of other elements.
 * @param props.isSmall      A boolean flag indicating whether to apply small padding within the list.
 * @param props.position     The position of the option list relative to its container (`left` or `right`).
 * @param props.testId       A unique identifier for testing purposes.
 * @param props.hasScroll    A boolean flag indicating whether the option list should be scrollable.
 * @returns A JSX element representing the structured layout of the option list with animation and scrollable view.
 *
 * @example
 * ```tsx
 * <OptionList height={200} position="right">
 *   <OptionItem label="Option 1" value="1" />
 *   <OptionItem label="Option 2" value="2" />
 * </OptionList>
 * ```
 */
const OptionList = ({
    children,
    hasFullWidth = false,
    hasScroll = true,
    height,
    isOnTop = false,
    offset = 0,
    position = 'left',
    testId = 'OptionList'
}: WithChildren<ComponentProps>) => {
    const {scrollRef} = useOptionList(isOnTop);

    return (
        <Wrapper
            $hasFullWidth={hasFullWidth}
            $isOnTop={isOnTop}
            $offset={offset}
            $position={position}
            animate="enter"
            custom={{
                isOnTop,
                position
            }}
            data-cy={testId}
            exit="exit"
            initial="initial"
            variants={FlyoutAnimation}
        >
            {hasScroll && (
                <CustomScrollSelect height={height} scrollRef={scrollRef}>
                    <PaddingWrapper $hasScroll={hasScroll} data-cy={`${testId}-listWrapper`}>
                        {children}
                    </PaddingWrapper>
                </CustomScrollSelect>
            )}
            {!hasScroll && (
                <PaddingWrapper $hasScroll={hasScroll} data-cy={`${testId}-listWrapper`}>
                    {children}
                </PaddingWrapper>
            )}
        </Wrapper>
    );
};

OptionList.displayName = 'OptionList';

export {OptionList};

interface WrapperProps {
    $hasFullWidth: boolean;
    $isOnTop: boolean;
    $offset: number;
    $position: 'center' | 'left' | 'right';
}

const Wrapper = styled(motion.div)<WrapperProps>`
    background-color: ${({theme}) => theme.colors.neutral0};
    border-radius: 8px;
    bottom: ${({$isOnTop, $offset}) => ($isOnTop ? null : `calc(-1 * ${spacing($offset)})`)};
    box-shadow: ${({theme}) => theme.boxShadows.popOverShadow};
    left: ${({$hasFullWidth, $position}) => ($position === 'left' && !$hasFullWidth ? `-${spacing(6)}` : '0')};
    left: ${({$hasFullWidth, $position}) => ($position === 'center' && !$hasFullWidth ? '50%' : null)};
    position: absolute;
    right: ${({$hasFullWidth, $position}) => ($position === 'right' && !$hasFullWidth ? `-${spacing(6)}` : null)};
    top: ${({$isOnTop, $offset}) => ($isOnTop ? `${spacing($offset)}` : null)};
    transform: ${({$hasFullWidth, $position}) => ($position === 'center' && !$hasFullWidth ? 'translateX(-50%)' : null)};
    width: ${({$hasFullWidth}) => ($hasFullWidth ? '100%' : 'max-content')};
    z-index: 10;
`;

interface PaddingWrapperProps {
    $hasScroll: boolean;
}

const PaddingWrapper = styled.div<PaddingWrapperProps>`
    display: flex;
    flex-direction: column;
    padding: ${spacing(2)} ${spacing(3)};
`;